import React, { Fragment, useState } from "react";
import Dialog from "@material-ui/core/Dialog";
import { useTheme } from '@material-ui/core/styles';
import { useDispatch, useSelector } from "react-redux";
import { useSetState } from "Helpers/Hooks";
import DialogContent from "@material-ui/core/DialogContent";
import Input from "Components/Input/Input";
import Grid from "@material-ui/core/Grid";
import Translator from "Languages/Utility/Translator";
import * as R from "ramda";
import MuiAutoComplete from "Components/MuiAutoComplete/MuiAutoComplete";
import { read } from "Helpers/LocalStorage";
import showNotify from "Helpers/Notify";
import ActionFormatter from "Components/Table/ActionFormatter/ActionFormatter";
import { EditKhedmatAction, GetAllKhedmat } from "Stores/actions";
import MuiSkeleton from "Components/MuiSkeleton/MuiSkeleton";
import UtilityService from "Services/http/endpoints/properties/UtilityService";
import StatusHandler from "Helpers/StatusHandler";
import MuiButton from "Components/MuiButton/MuiButton";
import { DialogActions } from "@material-ui/core";
import { useTranslation } from 'react-i18next';
import BackDropLoading from "Components/BackDropLoading/BackDropLoading";
import * as Icons from "react-bootstrap-icons";

function EditRowKhedmat({ row }) {
  const { direction } = useTheme();
  const { t } = useTranslation();
  const { khedmatDataPagination } = useSelector((state) => ({
    khedmatSys: state.khedmat.khedmatSys,
    khedmatDataPagination: state.khedmat.khedmatDataPagination
  }));
  const dispatch = useDispatch();
  const [formValid, setFormValid] = useState({});
  const [showDialog, setShowDialog] = useState(false);
  const [change, setChange] = useState(false);
  const [loading, setLoading] = useState(false);
  const [controls, setControls] = useSetState({
    sysGroup: {
      type: "select",
      selectItems: [],
      value: row.IdSys,
      validation: {
        required: true,
      },
      auth: true,
    },
    name: {
      type: "text",
      value: row.Name,
      validation: {
        required: true,
      },
      auth: true,
    },
    groupPrice: {
      type: "price",
      value: row.GroupPrice,
      validation: {
        required: true,
        isPrice: true,
        notZero: true,
      },
      auth: read("StypeU") === "1",
    },
    userPrice: {
      type: "price",
      value: row.UserPrice,
      validation: {
        required: true,
        isPrice: true,
        notZero: true,
      },
      auth: read("StypeU") === "2" || read("StypeU") === "5",
    },
  });

  const handleDialog = () => {
    setShowDialog(!showDialog);
  }

  const setValue = (name, value, validation) => {
    setControls({ [name]: { value: value } });
    setFormValid((prev) => ({ ...prev, [name]: validation }));
    setChange(true);
  }

  const autoCompleteHandler = async (e, value) => {
    const controlName = e.target.id.split("-")[0];
    setControls({ [controlName]: { value: value } });
    setFormValid((prev) => ({ ...prev, [controlName]: true }));
    setChange(true);
  }

  const onFinish = async (e) => {
    e.preventDefault();
    setLoading(true);
    if(change) {
      const { sysGroup, name, groupPrice, userPrice } = controls;
      if (!R.isEmpty(formValid) && Object.values(formValid).every((item) => item === true)) {
        let price;
        if (groupPrice.auth) price = +groupPrice.value;
        else price = +userPrice.value;
        try {
          const data = {
            KeyGroup: read("groupKey"),
            KeyUser: read("userKey"),
            Id: +row.Id,
            Name: name.value,
            IdSys: +sysGroup.value.value,
            Price: price,
          };
          await dispatch(EditKhedmatAction(data));
          const params = {
            KeyGroup: read("groupKey"),
            KeyUser: read("userKey"),
            Limit: khedmatDataPagination.Limit,
            Page: 1,
          };
          await dispatch(GetAllKhedmat(params));
          handleDialog();
          onClear();
        } catch (err) {
      console.error('[catch error]', err)
    }
      } else {
        showNotify({
          text: t('correctFormDataRequired'),
          customClass: "error",
        });
      }
    }
    setLoading(false);
  }

  const onEnter = async () => {
    onClear();
    try {
      setLoading(true);
      const params = {
        KeyGroup: read("groupKey"),
        KeyUser: read("userKey"),
      };
      const response = await UtilityService.GetKhedmatSys(params);
      let err = StatusHandler(response);
      if (!err.isFail) {
        const data = response.data.Value;
        const update = data.map((m) => {
          return {
            ...m,
            value: m.Id,
            label: m.Name,
          };
        });
        const groupValue = update.find((g) => g.value === row.IdSys);
        setControls({ sysGroup: { value: groupValue } });
        setControls({ sysGroup: { selectItems: update } });
      }
      setLoading(false);
    } catch (e) {
      setLoading(false);
    }
  };
  const onClear = () => {
    setFormValid({});
    setControls({ sysGroup: { value: row.IdSys } });
    setControls({ name: { value: row.Name } });
    setControls({ groupPrice: { value: row.GroupPrice } });
    setControls({ userPrice: { value: row.UserPrice } });
  };

  return (
    <>
      <BackDropLoading loading={loading} />
      <ActionFormatter
        title={<Translator id="virayesh" />}
        icon={<Icons.Pencil size={17} />}
        className="warning-fill"
        onClick={handleDialog}
        disabled={
          (row.IsG &&
              read("StypeU") !== "1") ||
              read("khadamatRules")?.accessUpdate === false ||
          row.Disable
        }
      />
      <Dialog
        dir={direction}
        open={showDialog}
        onClose={handleDialog}
        onEnter={onEnter}
        fullWidth
        maxWidth="xs"
      >
        <form onSubmit={onFinish}>
          <div className="dialogHeader">
            <p>
              <Translator id="virayesh_Khedmat" />
            </p>
          </div>
          <DialogContent>
            {
              !loading ? (
              <Grid container spacing={2} className="d-flex">
                {
                  Object.keys(controls).filter(key => controls[key].auth).map((key, index) => {
                    return (
                      <Grid item key={key + index} xs={12} style={{marginBottom: controls[key].type === "select" ? '10px' : '0px'}}>
                        {
                          controls[key].type === "text" &&
                          <Input
                            setValue={setValue.bind(this)}
                            name={key}
                            validation={controls[key].validation}
                            placeholder={t(key)}
                            label={<Translator id={key} />}
                            type="text"
                            value={controls[key].value}
                          />
                        }
                        {
                          controls[key].type === "price" &&
                          <Input
                            setValue={setValue.bind(this)}
                            value={controls[key].value}
                            name={key}
                            placeholder={t(key)}
                            validation={controls[key].validation}
                            label={<Translator id={key} />}
                            type={controls[key].type}
                            endIcon={<Translator id='moneyUnit' />}
                          />
                        }
                        {
                          controls[key].type === "select" &&
                          <MuiAutoComplete
                            loading={loading}
                            options={controls[key].selectItems}
                            label={<Translator id={key} />}
                            value={controls[key].value}
                            id={key}
                            onChange={autoCompleteHandler}
                          />
                        }
                      </Grid>
                    );
                  })
                }
              </Grid>) : <MuiSkeleton xs={12} number={3}/>
            }
          </DialogContent>
          <DialogActions>
            <MuiButton
              color="primary"
              type="submit"
              title={<Translator id="button_save" />}
            />
            <MuiButton
              onClick={handleDialog}
              title={<Translator id="enseraf" />}
            />
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
}

export default EditRowKhedmat;
